import { CheckCircle, HourglassLow, Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo } from 'react';

import { RegisterLink } from '@/components/RegisterLink/RegisterLink';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryCatalogOnly,
    getItemSummaryIsAvailable,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { GlobalState } from '@/redux/store';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    catalogId: number;
    itemId: number;
};

const bannerBaseStyles =
    'flex h-[26px] items-center justify-center text-center font-primary font-semibold tracking-[1px] uppercase';

const CardCtaBannerComponent = (props: Props) => {
    const { catalogId, itemId } = props;
    const formatMessage = useAppFormatMessage();
    const isCatalogOnly = useAppSelector((state: GlobalState) => getItemSummaryCatalogOnly(state, itemId));
    const isAvailable = useAppSelector((state: GlobalState) => getItemSummaryIsAvailable(state, itemId));
    const {
        approved = false,
        declined = false,
        pending = false,
        registered = false,
    } = useAppSelector((state) => getCatalogRegistration(state, catalogId));

    if (!isAvailable) {
        return null;
    } else if (approved) {
        return (
            <div className={cn(bannerBaseStyles, 'text-text-success')}>
                <CheckCircle
                    className="mr-xs"
                    size="xs"
                    style="fill"
                />
                <span className="inline-block h-[26px] w-[90px] overflow-hidden leading-[26px] [word-break:keep-all]">
                    {formatMessage(['approved_to_bid'])}
                </span>
            </div>
        );
    } else if (declined) {
        return (
            <div className={cn(bannerBaseStyles, 'text-text-critical')}>
                <Warning
                    className="mr-xs"
                    color="critical"
                    size="sm"
                    style="fill"
                />
                {formatMessage(['registration_status_label', 'registration_declined'])}
            </div>
        );
    } else if (pending && isCatalogOnly) {
        return (
            <div className={cn(bannerBaseStyles, 'text-text-secondary')}>
                <HourglassLow
                    className="mr-xs"
                    size="sm"
                    style="fill"
                />
                <span className="flex h-[26px] w-[74px] flex-col-reverse overflow-hidden leading-[26px] [word-break:keep-all]">
                    {formatMessage(['registration_status_label', 'catalog_only_registration_submitted'])}
                </span>
            </div>
        );
    } else if (pending) {
        return (
            <div className={cn(bannerBaseStyles, 'text-text-secondary')}>
                <HourglassLow
                    className="mr-xs"
                    size="sm"
                    style="fill"
                />
                <span className="flex h-[26px] w-[74px] flex-col-reverse overflow-hidden leading-[26px] [word-break:keep-all]">
                    {formatMessage(['registration_status_label', 'registration_pending'])}
                </span>
            </div>
        );
    } else if (!registered) {
        return (
            <div className={bannerBaseStyles}>
                <RegisterLink
                    className="normal-case"
                    catalogId={catalogId}
                />
            </div>
        );
    }

    return null;
};

export const CardCtaBanner = memo<Props>(CardCtaBannerComponent);
